import { Plan } from '../types';

interface Props {
  form: Omit<Plan, 'id'>;
  set: (f: Partial<Omit<Plan, 'id'>>) => void;
}

const Toggle = ({ value, onChange }: { value: boolean; onChange: (v: boolean) => void }) => (
  <button type="button" onClick={() => onChange(!value)} className={`w-11 h-6 rounded-full transition-colors cursor-pointer relative shrink-0 overflow-hidden ${value ? 'bg-violet-600' : 'bg-white/10'}`}>
    <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${value ? 'left-6' : 'left-1'}`} />
  </button>
);

interface FeatureRowProps {
  icon: string;
  label: string;
  hint?: string;
  value: boolean;
  onChange: (v: boolean) => void;
  tag?: string;
}

const FeatureRow = ({ icon, label, hint, value, onChange, tag }: FeatureRowProps) => (
  <div className={`flex items-center justify-between py-3 border-b border-white/5 last:border-0 rounded-lg px-2 transition-colors ${value ? '' : 'opacity-60'}`}>
    <div className="flex items-center gap-3">
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${value ? 'bg-violet-600/20' : 'bg-white/5'}`}>
        <i className={`${icon} text-[14px] ${value ? 'text-violet-400' : 'text-white/30'}`} />
      </div>
      <div>
        <div className="flex items-center gap-2">
          <p className="text-[13px] text-white/80">{label}</p>
          {tag && <span className="text-[10px] px-1.5 py-0.5 bg-cyan-500/20 text-cyan-400 rounded">{tag}</span>}
        </div>
        {hint && <p className="text-[11px] text-white/30">{hint}</p>}
      </div>
    </div>
    <Toggle value={value} onChange={onChange} />
  </div>
);

const PlanEditorFeatures = ({ form, set }: Props) => {
  const enabledCount = [
    form.reports_enabled, form.can_export_csv, form.can_export_pdf,
    form.can_access_advanced_insights, form.can_use_multiple_currencies,
    form.can_access_loans_module, form.can_access_recurring_advanced,
    form.can_access_priority_support, form.can_access_insights_module,
    form.can_access_historical_insights, form.can_access_recommendations,
    form.can_use_ai_assistant,
  ].filter(Boolean).length;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-[12px] text-white/40">Recursos e módulos habilitados para este plano</p>
        <span className="text-[12px] px-3 py-1 bg-violet-600/20 text-violet-400 rounded-full">
          {enabledCount} habilitados
        </span>
      </div>

      {/* Insights Module */}
      <div className="bg-white/3 border border-white/8 rounded-2xl p-4 mb-4">
        <p className="text-[11px] text-white/30 uppercase tracking-wider font-600 mb-2">Módulo de Insights</p>
        <FeatureRow
          icon="ri-lightbulb-flash-line" label="Acesso ao módulo Insights"
          hint="Habilita o menu Insights na área do cliente"
          value={form.can_access_insights_module} onChange={(v) => set({ can_access_insights_module: v })}
        />
        <FeatureRow
          icon="ri-history-line" label="Histórico dos últimos 6 meses"
          hint="Gráfico de evolução financeira histórica"
          value={form.can_access_historical_insights} onChange={(v) => set({ can_access_historical_insights: v })}
        />
        <FeatureRow
          icon="ri-medal-line" label="Recomendações personalizadas"
          hint="Sugestões de ações baseadas nos dados reais do usuário"
          value={form.can_access_recommendations} onChange={(v) => set({ can_access_recommendations: v })}
          tag="Premium"
        />
        {/* Insights level selector */}
        <div className="flex items-center justify-between py-3 px-2">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-violet-600/20">
              <i className="ri-bar-chart-grouped-line text-[14px] text-violet-400" />
            </div>
            <div>
              <p className="text-[13px] text-white/80">Nível de insights</p>
              <p className="text-[11px] text-white/30">Define a profundidade das análises exibidas</p>
            </div>
          </div>
          <select
            value={form.insights_level}
            onChange={(e) => set({ insights_level: e.target.value as 'basic' | 'advanced' | 'full' })}
            className="bg-white/8 text-white/70 text-[12px] border border-white/10 rounded-lg px-2 py-1 cursor-pointer"
          >
            <option value="basic">Básico</option>
            <option value="advanced">Avançado</option>
            <option value="full">Completo</option>
          </select>
        </div>
        {/* Max insights per period */}
        <div className="flex items-center justify-between py-3 px-2">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-violet-600/20">
              <i className="ri-list-check-3 text-[14px] text-violet-400" />
            </div>
            <div>
              <p className="text-[13px] text-white/80">Máx. insights por período</p>
              <p className="text-[11px] text-white/30">Limite de padrões exibidos no módulo</p>
            </div>
          </div>
          <input
            type="number"
            min={1}
            max={50}
            value={form.max_insights_per_period}
            onChange={(e) => set({ max_insights_per_period: Number(e.target.value) })}
            className="bg-white/8 text-white/70 text-[12px] border border-white/10 rounded-lg px-2 py-1 w-16 text-center"
          />
        </div>
      </div>

      {/* Reports & Export */}
      <div className="bg-white/3 border border-white/8 rounded-2xl p-4 mb-4">
        <p className="text-[11px] text-white/30 uppercase tracking-wider font-600 mb-2">Relatórios e Exportação</p>
        <FeatureRow
          icon="ri-bar-chart-line" label="Relatórios avançados"
          hint="Dashboard analítico detalhado por categoria, período e tendências"
          value={form.reports_enabled} onChange={(v) => set({ reports_enabled: v })}
        />
        <FeatureRow
          icon="ri-file-excel-line" label="Exportar para CSV"
          hint="Download de lançamentos em formato planilha"
          value={form.can_export_csv} onChange={(v) => set({ can_export_csv: v })}
        />
        <FeatureRow
          icon="ri-file-pdf-line" label="Exportar para PDF"
          hint="Relatórios formatados em PDF"
          value={form.can_export_pdf} onChange={(v) => set({ can_export_pdf: v })}
        />
        <FeatureRow
          icon="ri-lightbulb-line" label="Insights avançados com IA"
          hint="Análise automática de padrões de gastos e sugestões"
          value={form.can_access_advanced_insights} onChange={(v) => set({ can_access_advanced_insights: v })}
          tag="IA"
        />
      </div>

      {/* Features & Modules */}
      <div className="bg-white/3 border border-white/8 rounded-2xl p-4 mb-4">
        <p className="text-[11px] text-white/30 uppercase tracking-wider font-600 mb-2">Recursos e Módulos</p>
        <FeatureRow
          icon="ri-global-line" label="Multi-moedas"
          hint="Contas e lançamentos em diferentes moedas"
          value={form.can_use_multiple_currencies} onChange={(v) => set({ can_use_multiple_currencies: v })}
        />
        <FeatureRow
          icon="ri-money-dollar-circle-line" label="Módulo de empréstimos e financiamentos"
          hint="Controle de parcelas, juros e amortização"
          value={form.can_access_loans_module} onChange={(v) => set({ can_access_loans_module: v })}
        />
        <FeatureRow
          icon="ri-repeat-line" label="Recorrência avançada"
          hint="Parcelas, financiamentos, duração e dia de vencimento"
          value={form.can_access_recurring_advanced} onChange={(v) => set({ can_access_recurring_advanced: v })}
        />
      </div>

      {/* Support */}
      <div className="bg-white/3 border border-white/8 rounded-2xl p-4 mb-4">
        <p className="text-[11px] text-white/30 uppercase tracking-wider font-600 mb-2">Suporte</p>
        <FeatureRow
          icon="ri-customer-service-line" label="Suporte prioritário"
          hint="Atendimento com prioridade e SLA garantido"
          value={form.can_access_priority_support} onChange={(v) => set({ can_access_priority_support: v })}
          tag="Premium"
        />
      </div>

      {/* Panda AI */}
      <div className="bg-white/3 border border-white/8 rounded-2xl p-4 mb-4">
        <p className="text-[11px] text-white/30 uppercase tracking-wider font-600 mb-2">Panda AI — Assistente Financeiro</p>
        <FeatureRow
          icon="ri-robot-2-line" label="Acesso ao Panda AI"
          hint="Assistente financeiro com IA respondendo com dados do usuário"
          value={form.can_use_ai_assistant} onChange={(v) => set({ can_use_ai_assistant: v })}
          tag="IA"
        />
        <FeatureRow
          icon="ri-sparkling-2-line" label="Insights avançados via IA"
          hint="Análises preditivas e recomendações personalizadas"
          value={form.can_use_ai_advanced_insights} onChange={(v) => set({ can_use_ai_advanced_insights: v })}
          tag="Premium"
        />
        <FeatureRow
          icon="ri-alarm-warning-line" label="Alertas proativos via IA"
          hint="IA detecta padrões e envia alertas automáticos"
          value={form.can_use_ai_proactive_alerts} onChange={(v) => set({ can_use_ai_proactive_alerts: v })}
          tag="Premium"
        />
        {/* Monthly questions limit */}
        <div className={`flex items-center justify-between py-3 px-2 transition-opacity ${!form.can_use_ai_assistant ? 'opacity-30 pointer-events-none' : ''}`}>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-emerald-600/20">
              <i className="ri-question-answer-line text-[14px] text-emerald-400" />
            </div>
            <div>
              <p className="text-[13px] text-white/80">Perguntas/mês</p>
              <p className="text-[11px] text-white/30">-1 = ilimitado</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="number" min={-1}
              value={form.ai_monthly_questions_limit}
              onChange={(e) => set({ ai_monthly_questions_limit: Number(e.target.value) })}
              className="bg-white/8 text-white/70 text-[12px] border border-white/10 rounded-lg px-2 py-1 w-20 text-center"
            />
            <span className={`text-[11px] px-2 py-0.5 rounded-full ${form.ai_monthly_questions_limit === -1 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-white/5 text-white/30'}`}>
              {form.ai_monthly_questions_limit === -1 ? '∞' : `máx ${form.ai_monthly_questions_limit}`}
            </span>
          </div>
        </div>
      </div>

      {/* Summary chips */}
      <div className="mt-4 p-4 bg-white/3 border border-white/8 rounded-2xl">
        <p className="text-[11px] text-white/30 uppercase tracking-wider font-600 mb-3">Recursos ativos</p>
        <div className="flex flex-wrap gap-2">
          {form.can_access_insights_module && <span className="text-[11px] px-2.5 py-1 bg-violet-600/20 text-violet-400 rounded-full flex items-center gap-1"><i className="ri-lightbulb-flash-line" />Insights ({form.insights_level})</span>}
          {form.can_access_historical_insights && <span className="text-[11px] px-2.5 py-1 bg-violet-600/20 text-violet-400 rounded-full flex items-center gap-1"><i className="ri-history-line" />Histórico</span>}
          {form.can_access_recommendations && <span className="text-[11px] px-2.5 py-1 bg-violet-600/20 text-violet-400 rounded-full flex items-center gap-1"><i className="ri-medal-line" />Recomendações</span>}
          {form.reports_enabled && <span className="text-[11px] px-2.5 py-1 bg-violet-600/20 text-violet-400 rounded-full flex items-center gap-1"><i className="ri-bar-chart-line" />Relatórios</span>}
          {form.can_export_csv && <span className="text-[11px] px-2.5 py-1 bg-violet-600/20 text-violet-400 rounded-full flex items-center gap-1"><i className="ri-file-excel-line" />CSV</span>}
          {form.can_export_pdf && <span className="text-[11px] px-2.5 py-1 bg-violet-600/20 text-violet-400 rounded-full flex items-center gap-1"><i className="ri-file-pdf-line" />PDF</span>}
          {form.can_access_advanced_insights && <span className="text-[11px] px-2.5 py-1 bg-violet-600/20 text-violet-400 rounded-full flex items-center gap-1"><i className="ri-lightbulb-line" />Insights IA</span>}
          {form.can_use_multiple_currencies && <span className="text-[11px] px-2.5 py-1 bg-violet-600/20 text-violet-400 rounded-full flex items-center gap-1"><i className="ri-global-line" />Multi-moedas</span>}
          {form.can_access_loans_module && <span className="text-[11px] px-2.5 py-1 bg-violet-600/20 text-violet-400 rounded-full flex items-center gap-1"><i className="ri-money-dollar-circle-line" />Empréstimos</span>}
          {form.can_access_recurring_advanced && <span className="text-[11px] px-2.5 py-1 bg-violet-600/20 text-violet-400 rounded-full flex items-center gap-1"><i className="ri-repeat-line" />Recorrência+</span>}
          {form.can_access_priority_support && <span className="text-[11px] px-2.5 py-1 bg-violet-600/20 text-violet-400 rounded-full flex items-center gap-1"><i className="ri-customer-service-line" />Suporte prio.</span>}
          {enabledCount === 0 && <span className="text-[11px] text-white/30">Nenhum recurso habilitado</span>}
        </div>
      </div>
    </div>
  );
};

export default PlanEditorFeatures;
